import { useState, useRef, useEffect } from "react";
import { useAssistantOrchestrator } from "./useCoachOrchestrator";
import { useCoachChatUiStore } from "../../stores/coachChatUiStore";
import WidgetRenderer from "./widgets/WidgetRenderer";
import type { JourneyPhase } from "../../generated-api";
import { type JourneyPhaseKind } from "./types";
import { JourneyInfo } from "./JourneyInfo";

// Turns the raw journey phase into a readable context block for the chat input.
function prettifyContext(phase: JourneyPhase | null): string {
  if (!phase) return "";
  const lines = [`Phase: ${phase.kind.replace(/_/g, " ")}`];
  if (phase.current_program?.title) {
    lines.push(`Program: ${phase.current_program.title}`);
  }
  const targets = phase.current_targets ?? [];
  if (targets.length) {
    lines.push(
      `Targets: ${targets
        .map(
          (t) =>
            `${t.kineticDomain} (${t.capacityQualitative} -> ${t.targetState})`,
        )
        .join("; ")}`,
    );
  }
  if (phase.recent_completions.length) {
    lines.push(`Recent completions: ${phase.recent_completions.length}`);
  }
  return `[Context]\n${lines.join("\n")}\n[/Context]`;
}

// Short explanation of what the coach can help with in the current phase.
function describePhase(phase: JourneyPhase | null): string {
  switch (phase?.kind) {
    case "profile_incomplete":
      return "Tell me a bit about yourself and what you're working on, and I'll tailor your journey.";
    case "set_targets":
      return "Let's define what you want to improve. Describe your goals and I'll turn them into targets.";
    case "assess_current_condition": {
      const target = phase.current_targets?.[0];
      return target
        ? `Let's assess your ${target.kineticDomain} to establish a baseline.`
        : "Assess where you are today so we have a clear starting point.";
    }
    case "generate_program": {
      const target = phase.current_targets?.[0];
      return target
        ? `Your ${target.kineticDomain} target is set. I can build a first program around it.`
        : "Your targets are set. I can build a first program around them.";
    }
    case "adjust_program": {
      const title = phase.current_program?.title;
      return title
        ? `"${title}" needs a review based on your recent performance. I can help adjust it.`
        : "Your current program needs a review based on your recent performance. I can help adjust it.";
    }
    case "assess_program_outcome": {
      const title = phase.current_program?.title;
      return title
        ? `"${title}"'s period has ended. I can help you evaluate your progress.`
        : "Your program period has ended. I can help you evaluate your progress.";
    }
    case "perform": {
      const title = phase.current_program?.title;
      return title
        ? `You're following "${title}". Ask me about exercises, progress, or how you're feeling.`
        : "You're following your program. Ask me about exercises, progress, or how you're feeling.";
    }
    default:
      return "Your personal movement coach. Ask me anything about your training.";
  }
}

export default function AiCoachChat({
  journeyPhaseOverrideKind,
  conversationStarterOverride,
  variant = "fullscreen",
  isExpandedOverride,
  onExpand,
  onCollapse,
  pageMatchActive,
  defaultPhaseLabel,
  onDismissPageMatch,
  onRevertToDefault,
}: {
  journeyPhaseOverrideKind?: JourneyPhase["kind"] | null;
  conversationStarterOverride?: string | null;
  // "fullscreen" (default): existing fixed inset-0 overlay, driven by the
  // legacy coachChatUiStore. "panel": fills its parent (used inside
  // ChatSidebar), driven by the controlled props below instead.
  variant?: "fullscreen" | "panel";
  isExpandedOverride?: boolean;
  onExpand?: () => void;
  onCollapse?: () => void;
  // Mismatch banner (panel variant only): true while the current page isn't
  // the page for the state machine's recommended phase.
  pageMatchActive?: boolean;
  // Human-readable label of that recommended phase, shown in the banner
  // instead of the word "default".
  defaultPhaseLabel?: string | null;
  onDismissPageMatch?: () => void;
  onRevertToDefault?: () => void;
} = {}) {
  const {
    journeyPhase: activeJourneyPhase,
    messages,
    machineState,
    errorMessage,
    suggestedPrompts,
    send,
    completeWidget,
  } = useAssistantOrchestrator({
    journeyPhaseOverrideKind,
    conversationStarterOverride,
  });
  const [input, setInput] = useState("");
  const legacyIsExpanded = useCoachChatUiStore((s) => s.isExpanded);
  const legacyExpand = useCoachChatUiStore((s) => s.open);
  const legacyCollapse = useCoachChatUiStore((s) => s.close);
  const isControlled = variant === "panel";
  const isExpanded = isControlled
    ? (isExpandedOverride ?? true)
    : legacyIsExpanded;
  const expandChat = isControlled ? (onExpand ?? (() => {})) : legacyExpand;
  const collapseChat = isControlled
    ? (onCollapse ?? (() => {}))
    : legacyCollapse;
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const isLoading =
    machineState === "awaiting_llm" || machineState === "running_actions";

  // Auto-scrolls to the newest messages when open
  useEffect(() => {
    if (isExpanded) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isExpanded, isLoading]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    setInput("");
    if (pageMatchActive) onDismissPageMatch?.();
    await send(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Header content logic
  let headerTitle = "AI Coach";
  let headerBadge: string | null = null;
  if (activeJourneyPhase?.kind === "set_targets") {
    headerTitle = "Tell me about your journey";
  } else if (activeJourneyPhase?.kind === "generate_program") {
    headerTitle = "Create your first program";
  } else if (
    ["adjust_program", "assess_program_outcome", "perform"].includes(
      activeJourneyPhase?.kind ?? "",
    )
  ) {
    headerTitle = activeJourneyPhase?.current_program?.title ?? "Program";
    headerBadge = activeJourneyPhase?.kind.replace(/_/g, " ") || null;
  } else {
    headerTitle = "Discuss next steps";
  }

  // Minimized: standalone preview, no header, no starter message.
  // Prompts come from the backend phase config (bootstrap `suggestedPrompts`).
  // Selecting a prompt opens the chat with context + prompt pre-filled (not sent).
  if (!isExpanded) {
    return (
      <DumbPreviewCard
        description={describePhase(activeJourneyPhase)}
        quickPrompts={suggestedPrompts}
        activeKind={activeJourneyPhase?.kind}
        disabled={machineState === "booting"}
        onActivate={() => expandChat()}
        onSelectPrompt={(p) => {
          const context = prettifyContext(activeJourneyPhase);
          setInput(context ? `${context}\n\n${p}` : p);
          expandChat();
        }}
      />
    );
  }

  const outerClassName =
    variant === "panel"
      ? "w-full h-full bg-bg-secondary flex flex-col overflow-hidden"
      : "fixed inset-0 z-50 bg-bg-secondary flex justify-center";
  const innerClassName =
    variant === "panel"
      ? "w-full h-full flex flex-col overflow-hidden"
      : "w-full md:max-w-screen-md h-[100dvh] flex flex-col overflow-hidden";

  return (
    <div className={outerClassName}>
      <div className={innerClassName}>
        {/* Header (expanded) */}
        <div className="px-4 py-3 bg-bg-secondary flex items-center justify-between gap-2 shrink-0 select-none relative">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              aria-label="Close"
              onClick={() => collapseChat()}
              className="p-1 rounded hover:bg-neutral-200 dark:hover:bg-neutral-800 text-text-secondary"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <div className="font-semibold text-text-primary text-sm sm:text-base truncate">
              {headerTitle}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {headerBadge && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-600/10 text-indigo-600 font-medium whitespace-nowrap">
                {headerBadge}
              </span>
            )}
            <JourneyInfo
              activeKind={activeJourneyPhase?.kind}
              confirmClear={messages.length > 1}
            />
          </div>
        </div>

        <>
          {/* Full Messages container (starter message at index 0 is replaced by prompts) */}
          <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3 bg-bg-secondary">
            <div className="flex flex-col gap-2 px-2 pt-2 pb-1 text-center select-none">
              <p className="text-sm text-text-secondary/70 leading-snug">
                {describePhase(activeJourneyPhase)}
              </p>
            </div>

            {suggestedPrompts.length > 0 && (
              <div className="flex flex-wrap gap-2 px-2">
                {suggestedPrompts.map((p) => (
                  <button
                    key={p}
                    type="button"
                    disabled={isLoading || machineState === "booting"}
                    onClick={() => {
                      const context = prettifyContext(activeJourneyPhase);
                      setInput(context ? `${context}\n\n${p}` : p);
                    }}
                    className="text-xs px-2 py-1 rounded-full bg-indigo-600/10 text-indigo-600 font-medium text-left disabled:opacity-50"
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}

            {messages.slice(1).map((msg, i) => (
              <div
                key={i}
                className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                    msg.role === "user"
                      ? "bg-indigo-600 text-white rounded-br-sm"
                      : "bg-bg-primary text-text-primary rounded-bl-sm border border-border-subtle"
                  }`}
                >
                  {msg.content}
                </div>
                {msg.widgets?.map((widget, wi) => (
                  <div key={wi} className="w-full max-w-[85%] mt-1">
                    <WidgetRenderer
                      widget={widget}
                      onComplete={(result) =>
                        completeWidget(widget.kind, result)
                      }
                      journeyPhase={activeJourneyPhase}
                    />
                  </div>
                ))}
              </div>
            ))}

            {isLoading && (
              <div className="flex items-start">
                <div className="bg-bg-primary border border-border-subtle rounded-2xl rounded-bl-sm px-3 py-2">
                  <span className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-text-secondary animate-bounce [animation-delay:0ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-text-secondary animate-bounce [animation-delay:150ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-text-secondary animate-bounce [animation-delay:300ms]" />
                  </span>
                </div>
              </div>
            )}

            {machineState === "error" && (
              <div className="text-xs text-red-500 text-center">
                {errorMessage ?? "Something went wrong. Please try again."}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Context-mismatch banner: phase was auto-switched to match this page. */}
          {pageMatchActive && (
            <div className="px-4 py-2 border-t border-border-subtle bg-indigo-600/10 flex items-center justify-between gap-2 shrink-0 text-xs text-indigo-600">
              <span className="truncate">
                {defaultPhaseLabel
                  ? `Your journey is at "${defaultPhaseLabel}"`
                  : "This page doesn't match your current journey step"}
              </span>
              <button
                type="button"
                onClick={() => onRevertToDefault?.()}
                className="shrink-0 font-semibold underline hover:no-underline"
              >
                {defaultPhaseLabel ? `Go to ${defaultPhaseLabel}` : "Go there"}
              </button>
            </div>
          )}

          {/* Active Chat Input Dock */}
          <div className="px-4 py-3 border-t border-border-subtle bg-bg-secondary flex gap-2 shrink-0 pb-safe">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading || machineState === "booting"}
              placeholder={
                machineState === "booting" ? "Loading..." : "Type a message..."
              }
              autoFocus
              rows={Math.min(8, Math.max(1, input.split("\n").length))}
              className="flex-1 bg-bg-secondary bg-surface-elevated border border-border-subtle rounded-lg px-3 py-2 text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-1 focus:ring-indigo-600 disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={
                isLoading || !input.trim() || machineState === "booting"
              }
              className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg disabled:opacity-40 hover:bg-indigo-700 transition-colors"
            >
              Send
            </button>
          </div>
        </>
      </div>
    </div>
  );
}

function DumbPreviewCard({
  description,
  quickPrompts,
  activeKind,
  disabled,
  onActivate,
  onSelectPrompt,
}: {
  description: string;
  quickPrompts: string[];
  activeKind?: JourneyPhaseKind;
  disabled: boolean;
  onActivate: () => void;
  onSelectPrompt: (prompt: string) => void;
}) {
  return (
    <div className="w-full flex flex-col gap-3 p-5 bg-bg-primary rounded-lg shadow-sm border border-border">
      <div className="flex justify-end">
        <JourneyInfo activeKind={activeKind} />
      </div>

      <div className="flex flex-col items-center gap-2 px-6 py-6 text-center select-none">
        <p className="text-base sm:text-lg text-text-secondary/70 leading-snug">
          {description}
        </p>
      </div>

      {quickPrompts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {quickPrompts.map((p) => (
            <button
              key={p}
              type="button"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onSelectPrompt(p);
              }}
              className="text-xs px-2 py-1 rounded-full bg-indigo-600/10 text-indigo-600 font-medium text-left disabled:opacity-50"
            >
              {p}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          readOnly
          disabled={disabled}
          onFocus={onActivate}
          onClick={onActivate}
          placeholder={disabled ? "Loading..." : "Ask your coach..."}
          className="flex-1 cursor-text bg-bg-secondary border border-border-subtle rounded-lg px-3 py-1.5 text-xs text-text-primary placeholder:text-text-secondary disabled:opacity-60"
        />
        <div className="px-3 py-1.5 bg-indigo-600/50 text-white text-xs font-medium rounded-lg">
          Send
        </div>
      </div>
    </div>
  );
}
